import { route, SandboxError, type Route } from '../adapter';
import { newId, now, paginate, type SandboxStore } from '../store';
import type { EventNote, NoteWrite } from '@/lib/events';

/** Notes on the event, with their templates. */

function noteIndex(store: SandboxStore, id: string): number {
  const index = store.notes.findIndex((note) => note._id === id);
  if (index < 0) throw new SandboxError(404, 'Note not found.');
  return index;
}

function author(store: SandboxStore): EventNote['owner'] {
  return store.users[0] ?? 'You';
}

export const noteRoutes: readonly Route[] = [
  route('GET', '/events/:id/notes', ({ store, query }) => paginate(store.notes, query, 200)),
  route('POST', '/events/:id/notes', ({ store, body }) => {
    const write = body as NoteWrite;
    const note: EventNote = {
      _id: newId(),
      category: write.category,
      subject: write.subject,
      body: write.body,
      ...(write.templateKey ? { templateKey: write.templateKey, templateVersion: store.templates.find((template) => template.key === write.templateKey)?.version ?? 1 } : {}),
      ...(write.status ? { status: write.status } : {}),
      ...(write.values ? { values: write.values } : {}),
      owner: author(store),
      createdAt: now(),
      updatedAt: now(),
    };
    store.notes = [note, ...store.notes];
    return { data: note };
  }),
  route('PUT', '/notes/:noteId', ({ store, params, body }) => {
    const write = body as NoteWrite;
    const index = noteIndex(store, params['noteId'] ?? '');
    const existing = store.notes[index] as EventNote;
    store.notes[index] = { ...existing, category: write.category, subject: write.subject, body: write.body, templateKey: write.templateKey ?? undefined, status: write.status ?? undefined, values: write.values ?? undefined, updatedBy: author(store), updatedAt: now() };
    return { data: store.notes[index] };
  }),
  route('POST', '/notes/:noteId/archive', ({ store, params }) => {
    const index = noteIndex(store, params['noteId'] ?? '');
    store.notes[index] = { ...(store.notes[index] as EventNote), archivedAt: now(), archivedBy: author(store) };
    return { data: store.notes[index] };
  }),
  route('POST', '/notes/:noteId/restore', ({ store, params }) => {
    const index = noteIndex(store, params['noteId'] ?? '');
    store.notes[index] = { ...(store.notes[index] as EventNote), archivedAt: undefined, archivedBy: undefined };
    return { data: store.notes[index] };
  }),
  route('POST', '/notes/:noteId/pin', ({ store, params }) => {
    const index = noteIndex(store, params['noteId'] ?? '');
    store.notes[index] = { ...(store.notes[index] as EventNote), pinnedAt: now() };
    return { data: store.notes[index] };
  }),
  route('POST', '/notes/:noteId/unpin', ({ store, params }) => {
    const index = noteIndex(store, params['noteId'] ?? '');
    store.notes[index] = { ...(store.notes[index] as EventNote), pinnedAt: undefined };
    return { data: store.notes[index] };
  }),
];
